import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Button, Input, Select } from 'antd';
import { useIntl } from 'react-intl';
import DrawerModal from '../../shared/DrawerModal';
import styles from './ClaimDamageModal.styles';
import {
  clearEditingClaim,
  closeModal,
  getIsVisibleModal,
  selectEditingClaim,
} from '../../../redux/store/ui/common';
import {
  fetchClaimsByCarId,
  updateClaimIfNeeded,
} from '../../../redux/store/userData/claims';
import { zIndex } from '../../../theme/theme';
import {
  damageWhereOptions,
  damageLocationOptionsFlat,
  damageSideOptionsFlat,
  damageComponentOptionsFlat,
  damageCharacteristicOptionsFlat,
  damageSizeOptionsFlat,
} from '../damageOptions';

const { Option } = Select;

interface DamageDiff {
  where?: string;
  component?: string;
  location?: string;
  side?: string;
  characteristics?: string;
  size?: string;
  characteristicsFreeText?: string;
  thirdPartyInvolved?: boolean | null;
  carRoadworthy?: boolean | null;
}

const TRI_STATE_UNKNOWN = '__unknown__';
const triStateToValue = (raw: string): boolean | undefined => {
  if (raw === 'yes') return true;
  if (raw === 'no') return false;
  return undefined;
};
const valueToTriState = (v: boolean | undefined): string => {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return TRI_STATE_UNKNOWN;
};

const ClaimDamageModal: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const visible = useSelector(getIsVisibleModal('ClaimDamageModal'));
  const editingClaim = useSelector(selectEditingClaim);
  const editingClaimData = editingClaim?.claim as any;
  const editingSource = editingClaim?.source;

  const [where, setWhere] = useState<string>('');
  const [component, setComponent] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [side, setSide] = useState<string>('');
  const [characteristics, setCharacteristics] = useState<string>('');
  const [size, setSize] = useState<string>('');
  const [thirdPartyInvolved, setThirdPartyInvolved] = useState<
    boolean | undefined
  >(undefined);
  const [carRoadworthy, setCarRoadworthy] = useState<boolean | undefined>(
    undefined
  );
  const [characteristicsFreeText, setCharacteristicsFreeText] =
    useState<string>('');

  useEffect(() => {
    if (visible) {
      setWhere(editingClaimData?.where || '');
      setComponent(editingClaimData?.component || '');
      setLocation(editingClaimData?.location || '');
      setSide(editingClaimData?.side || '');
      setCharacteristics(editingClaimData?.characteristics || '');
      setSize(editingClaimData?.size || '');
      setThirdPartyInvolved(editingClaimData?.thirdPartyInvolved);
      setCarRoadworthy(editingClaimData?.carRoadworthy);
      setCharacteristicsFreeText(
        editingClaimData?.characteristicsFreeText ||
          editingClaimData?.damageDescription ||
          ''
      );
    }
  }, [visible, editingClaimData]);

  const handleWhereChange = (value: string) => {
    setWhere(value);
    setLocation('');
    setSide('');
    setComponent('');
    setCharacteristics('');
    setSize('');
  };

  const handleLocationChange = (value: string) => {
    setLocation(value);
    setSide('');
    setComponent('');
    setCharacteristics('');
    setSize('');
  };

  const handleSideChange = (value: string) => {
    setSide(value);
    setComponent('');
    setCharacteristics('');
    setSize('');
  };

  const handleComponentChange = (value: string) => {
    setComponent(value);
    setCharacteristics('');
    setSize('');
  };

  const handleCharacteristicChange = (value: string) => {
    setCharacteristics(value);
    setSize('');
  };

  const handleCancel = () => {
    batch(() => {
      dispatch(closeModal('ClaimDamageModal'));
      dispatch(clearEditingClaim());
    });
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    const diff: DamageDiff = {};
    if (where !== (editingClaimData?.where || '')) {
      diff.where = where;
    }
    if (component !== (editingClaimData?.component || '')) {
      diff.component = component;
    }
    if (location !== (editingClaimData?.location || '')) {
      diff.location = location;
    }
    if (side !== (editingClaimData?.side || '')) {
      diff.side = side;
    }
    if (characteristics !== (editingClaimData?.characteristics || '')) {
      diff.characteristics = characteristics;
    }
    if (size !== (editingClaimData?.size || '')) {
      diff.size = size;
    }
    if (
      characteristicsFreeText !==
      (editingClaimData?.characteristicsFreeText || '')
    ) {
      diff.characteristicsFreeText = characteristicsFreeText;
    }
    if (thirdPartyInvolved !== editingClaimData?.thirdPartyInvolved) {
      diff.thirdPartyInvolved =
        thirdPartyInvolved === undefined ? null : thirdPartyInvolved;
    }
    if (carRoadworthy !== editingClaimData?.carRoadworthy) {
      diff.carRoadworthy = carRoadworthy === undefined ? null : carRoadworthy;
    }
    if (Object.keys(diff).length > 0 && editingClaimData?.id) {
      const carId = editingClaimData?.carId;
      const wasFromCarDrawer = editingSource === 'car-drawer';
      dispatch(
        updateClaimIfNeeded(editingClaimData.id, diff, () => {
          if (wasFromCarDrawer && carId) {
            dispatch(fetchClaimsByCarId(carId));
          }
        })
      );
    }
    handleCancel();
  };

  const renderReadonlyRow = (labelId: string, value?: string) => {
    if (!value) return null;
    return (
      <div className="readonly-row">
        <div className="label">{intl.formatMessage({ id: labelId })}</div>
        <div className="readonly-value">{value}</div>
      </div>
    );
  };

  return (
    <>
      <DrawerModal visible={visible} onClose={handleCancel}>
        <div
          data-testid="ClaimDamageModal-contents"
          className="claim-damage-contents"
        >
          <div className="claim-damage-title">
            {intl.formatMessage({ id: 'fleetadmin.claim.damage.edit' })}
          </div>

          {renderReadonlyRow(
            'fleetadmin.claim.damage.title',
            editingClaimData?.title
          )}

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({ id: 'fleetadmin.claim.where.label' })}
            </div>
            <Select
              data-testid="ClaimDamageModal-whereSelect"
              value={where || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.where.label',
              })}
              onChange={handleWhereChange}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
            >
              {damageWhereOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({ id: 'fleetadmin.claim.location.label' })}
            </div>
            <Select
              data-testid="ClaimDamageModal-locationSelect"
              value={location || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.location.label',
              })}
              onChange={handleLocationChange}
              disabled={!where}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              allowClear
            >
              {damageLocationOptionsFlat.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({ id: 'fleetadmin.claim.side.label' })}
            </div>
            <Select
              data-testid="ClaimDamageModal-sideSelect"
              value={side || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.side.label',
              })}
              onChange={handleSideChange}
              disabled={!location}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              allowClear
            >
              {damageSideOptionsFlat.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({ id: 'fleetadmin.claim.component.label' })}
            </div>
            <Select
              data-testid="ClaimDamageModal-componentSelect"
              value={component || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.component.label',
              })}
              onChange={handleComponentChange}
              disabled={!side}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
            >
              {damageComponentOptionsFlat.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.claim.characteristic.label',
              })}
            </div>
            <Select
              data-testid="ClaimDamageModal-characteristicSelect"
              value={characteristics || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.characteristic.label',
              })}
              onChange={handleCharacteristicChange}
              disabled={!component}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              allowClear
            >
              {damageCharacteristicOptionsFlat.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.claim.size.label',
              })}
            </div>
            <Select
              data-testid="ClaimDamageModal-sizeSelect"
              value={size || undefined}
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({
                id: 'fleetadmin.claim.size.label',
              })}
              onChange={(value: string) => setSize(value)}
              disabled={!characteristics}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
              allowClear
            >
              {damageSizeOptionsFlat.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {intl.formatMessage({ id: opt.label })}
                </Option>
              ))}
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({ id: 'fleetadmin.claim.3rdparty.label' })}
            </div>
            <Select
              data-testid="ClaimDamageModal-thirdPartyInvolvedSelect"
              value={valueToTriState(thirdPartyInvolved)}
              style={{ width: '100%' }}
              onChange={(raw: string) =>
                setThirdPartyInvolved(triStateToValue(raw))
              }
              dropdownStyle={{ zIndex: zIndex.dropDown }}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.claim.roadworthiness.label',
              })}
            </div>
            <Select
              data-testid="ClaimDamageModal-roadworthySelect"
              value={valueToTriState(carRoadworthy)}
              style={{ width: '100%' }}
              onChange={(raw: string) => setCarRoadworthy(triStateToValue(raw))}
              dropdownStyle={{ zIndex: zIndex.dropDown }}
            >
              <Option value="yes">Yes</Option>
              <Option value="no">No</Option>
            </Select>
          </div>

          <div className="claim-damage-row">
            <div className="label">
              {intl.formatMessage({
                id: 'fleetadmin.claim.characteristicsFreeText',
              })}
            </div>
            <Input
              data-testid="ClaimDamageModal-freeTextInput"
              value={characteristicsFreeText}
              maxLength={500}
              onChange={(e) => setCharacteristicsFreeText(e.target.value)}
            />
          </div>

          <div className="button-container">
            <Button
              data-testid="ClaimDamageModal-updateBtn"
              className="submit-button"
              onClick={handleSubmit}
            >
              {intl.formatMessage({ id: 'fleetadmin.admins.info.update' })}
            </Button>
            <Button className="cancel-button" onClick={handleCancel}>
              {intl.formatMessage({ id: 'fleetadmin.admins.info.cancel' })}
            </Button>
          </div>
        </div>
      </DrawerModal>
      <style jsx>{styles}</style>
    </>
  );
};

export default ClaimDamageModal;
